import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { UserNotification } from "@/lib/notifications";

const QUERY_KEY = ["user_notifications"] as const;
const PAGE_SIZE = 30;

export function useNotifications() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toastedIds = useRef<Set<string>>(new Set());

  const query = useQuery({
    queryKey: [...QUERY_KEY, user?.id ?? "anon"],
    queryFn: async (): Promise<UserNotification[]> => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("user_notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(PAGE_SIZE);
      if (error) throw error;
      return (data ?? []) as UserNotification[];
    },
    enabled: !!user,
    staleTime: 30_000,
  });

  // Seed toastedIds with the initial fetch so we never re-toast historical rows.
  useEffect(() => {
    if (!query.data) return;
    if (toastedIds.current.size === 0) {
      for (const n of query.data) toastedIds.current.add(n.id);
    }
  }, [query.data]);

  // Realtime subscription.
  // The supabase client reuses channels by topic name. If a previous instance
  // of this hook left a channel with the same topic still registered, calling
  // `.on('postgres_changes', ...)` on the reused channel throws
  // "cannot add postgres_changes callbacks ... after subscribe()".
  // Use a unique channel name per hook instance to avoid reuse, but keep the
  // postgres filter scoped to the signed-in user.
  const userId = user?.id ?? null;
  useEffect(() => {
    if (!userId) return;
    const uniqueId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2);
    const channel = supabase
      .channel(`user_notifications:${userId}:${uniqueId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "user_notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const row = payload.new as UserNotification;
          if (toastedIds.current.has(row.id)) return;
          toastedIds.current.add(row.id);

          // Push toast. Credit-threshold notifications get richer treatment
          // (severity + an action button that deep-links to the credits card)
          // so the user can react without hunting through Settings.
          const baseOpts = row.body ? { description: row.body } : undefined;
          if (row.kind === "billing.credits_exhausted") {
            toast.error(row.title, {
              ...baseOpts,
              duration: 12000,
              action: {
                label: "Buy credits",
                onClick: () =>
                  navigate("/settings/credits?buy_credits=1"),
              },
            });
          } else if (row.kind === "billing.credits_warning") {
            toast.warning(row.title, {
              ...baseOpts,
              duration: 10000,
              action: {
                label: "View credits",
                onClick: () => navigate("/settings/credits"),
              },
            });
          } else if (row.kind === "billing.credits_refreshed") {
            toast.success(row.title, {
              ...baseOpts,
              duration: 8000,
              action: {
                label: "View credits",
                onClick: () => navigate("/settings/credits"),
              },
            });
          } else if (/fail/i.test(row.kind)) {
            toast.error(row.title, baseOpts);
          } else {
            toast(row.title, baseOpts);
          }

          // Optimistically prepend to cached list
          qc.setQueryData<UserNotification[]>(
            [...QUERY_KEY, userId],
            (prev) => {
              const list = prev ?? [];
              if (list.some((n) => n.id === row.id)) return list;
              return [row, ...list].slice(0, PAGE_SIZE);
            },
          );
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "user_notifications",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          void qc.invalidateQueries({ queryKey: [...QUERY_KEY, userId] });
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId, qc, navigate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const notifications = query.data ?? [];
  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read_at).length,
    [notifications],
  );

  const markAllRead = useCallback(async () => {
    if (!user) return;
    const unread = notifications.filter((n) => !n.read_at).map((n) => n.id);
    if (unread.length === 0) return;
    const nowIso = new Date().toISOString();
    qc.setQueryData<UserNotification[]>(
      [...QUERY_KEY, user.id],
      (prev) => (prev ?? []).map((n) => (n.read_at ? n : { ...n, read_at: nowIso })),
    );
    const { error } = await supabase
      .from("user_notifications")
      .update({ read_at: nowIso })
      .in("id", unread);
    if (error) {
      void qc.invalidateQueries({ queryKey: [...QUERY_KEY, user.id] });
    }
  }, [user, notifications, qc]);

  const markRead = useCallback(
    async (id: string) => {
      if (!user) return;
      const nowIso = new Date().toISOString();
      qc.setQueryData<UserNotification[]>(
        [...QUERY_KEY, user.id],
        (prev) =>
          (prev ?? []).map((n) => (n.id === id && !n.read_at ? { ...n, read_at: nowIso } : n)),
      );
      const { error } = await supabase
        .from("user_notifications")
        .update({ read_at: nowIso })
        .eq("id", id);
      if (error) {
        void qc.invalidateQueries({ queryKey: [...QUERY_KEY, user.id] });
      }
    },
    [user, qc],
  );

  const remove = useCallback(
    async (id: string) => {
      if (!user) return;
      qc.setQueryData<UserNotification[]>(
        [...QUERY_KEY, user.id],
        (prev) => (prev ?? []).filter((n) => n.id !== id),
      );
      const { error } = await supabase.from("user_notifications").delete().eq("id", id);
      if (error) {
        void qc.invalidateQueries({ queryKey: [...QUERY_KEY, user.id] });
      }
    },
    [user, qc],
  );

  return {
    notifications,
    unreadCount,
    loading: query.isLoading,
    markRead,
    markAllRead,
    remove,
  };
}
